import type { CheckoutSessionData, GameData } from "@/interfaces";
import { useCallback, useState } from "react";
import { useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { getCheckoutSession } from "@/services/PaymentService";
import { getAppId, storeToken } from "@/lib/token-utils";
import {
  getGameDataWithFallback,
  augmentGameDataWithPortalUrl,
  isBackgroundEnabled,
} from "@/lib/game-config-utils";
import type { AxiosError } from "axios";
import { isShortUrl, parseShortUrl } from "@/lib/url-utils";
import { ShortUrlService } from "@/services/ShortUrlService";
import { getAppIdFromShortCode } from "@/lib/app-id-mapping";

export const useConfig = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Check both 'jwt' and 'token' parameters (jwt is used for wrapped flows to avoid PayPal conflict)
  const token = searchParams.get("jwt") || searchParams.get("token");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [currentGame, setCurrentGame] = useState<GameData>(
    getGameDataWithFallback(null)
  );
  const [stripePromise, setStripePromise] = useState<ReturnType<
    typeof loadStripe
  > | null>(null);
  const [isLandscape, setIsLandscape] = useState<boolean>(
    window.matchMedia("(orientation: landscape)").matches
  );
  const [sessionData, setSessionData] = useState<CheckoutSessionData | null>(
    null
  );
  const [isUnauthorized, setIsUnauthorized] = useState<boolean>(false);
  const [isShortUrlResolving, setIsShortUrlResolving] = useState<boolean>(false);
  const [shortUrlError, setShortUrlError] = useState<string | null>(null);

  // Check for short URL FIRST before processing tokens
  useEffect(() => {
    const pathname = location.pathname;
    
    // Check if this is a short URL
    if (isShortUrl(pathname)) {
      resolveShortUrlAndRedirect();
      return;
    }

    // Normal flow: handle token storage
    if (token && token.trim() !== "") {
      // CRITICAL: Backup the JWT token before anything can overwrite it
      sessionStorage.setItem('original_jwt_token', token);
      storeToken(token);
    } else {
      // Even if no token in URL, try to restore from backup
      const backupToken = sessionStorage.getItem('original_jwt_token');
      if (backupToken && backupToken.startsWith('eyJ')) {
        storeToken(backupToken);
      }
    }
  }, [location.pathname, token]);

  async function resolveShortUrlAndRedirect() {
    setIsShortUrlResolving(true);
    setShortUrlError(null);

    try {
      const pathname = location.pathname;
      const parts = parseShortUrl(pathname);

      if (!parts) {
        setShortUrlError("Invalid short URL format");
        return;
      }

      // Get app ID for loading screen (before API call)
      const appId = getAppIdFromShortCode(parts.appShortCode);
      if (!appId) {
        setShortUrlError("Unknown app code");
        return;
      }

      // Load game config to show correct loading screen
      const gameData = getGameDataWithFallback(appId);
      setCurrentGame(gameData);
      sessionStorage.setItem("gameData", JSON.stringify(gameData));

      // Resolve short URL
      const resolution = await ShortUrlService.resolveShortUrl(
        parts.appShortCode,
        parts.shortCode
      );

      // Verify resolution has required fields
      if (!resolution || !resolution.app_id || !resolution.query_params) {
        setShortUrlError("Invalid response from server");
        return;
      }

      // Verify app ID matches
      if (resolution.app_id !== appId) {
        setShortUrlError("App ID mismatch");
        return;
      }

      // Update browser URL without reload
      const newUrl = `/checkout?${resolution.query_params}`;
      navigate(newUrl, { replace: true });
    } catch (error: any) {
      setShortUrlError(error.message || "Failed to resolve short URL");
    } finally {
      setIsShortUrlResolving(false);
    }
  }

  useEffect(() => {
    const mediaQuery = window.matchMedia("(orientation: landscape)");
    const handleChange = (e: MediaQueryListEvent) => {
      setIsLandscape(e.matches);
    };
    mediaQuery.addEventListener("change", handleChange);
    const gameId = getAppId("storage");
    const customerPortalUrl = searchParams.get("customer_portal_url");
    let gameData: GameData;

    if (gameId) {
      gameData = getGameDataWithFallback(gameId);
    } else {
      const storedGameData = sessionStorage.getItem("gameData");
      if (storedGameData) {
        gameData = JSON.parse(storedGameData) as GameData;
      } else {
        gameData = getGameDataWithFallback(null);
      }
    }

    const gameDataWithPortal = augmentGameDataWithPortalUrl(
      gameData,
      customerPortalUrl
    );

    document.title = gameDataWithPortal.checkout_title;
    setCurrentGame(gameDataWithPortal);
    sessionStorage.setItem("gameData", JSON.stringify(gameDataWithPortal));
    if (gameDataWithPortal.stripe_public_key) {
      setStripePromise(loadStripe(gameDataWithPortal.stripe_public_key));
    }
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [searchParams, token]);

  useEffect(() => {
    if (!currentGame) return;

    // Check if background should be enabled based on hierarchy:
    // 1. URL query param (?bg=enabled|disabled)
    // 2. Wrapped PayPal flow (?flow=wrapped_paypal_vaulted) - always disable background
    // 3. Wrapped PayPal return (?paypal_status=vaulted_success or vaulted_cancelled) - always disable background
    // 4. PayPal redirect back to checkout (?paypal_token & ?PayerID) with native return URL - always disable background
    // 5. Game config bg field ("enabled"|"disabled")
    // 6. Default to enabled
    const bgQueryParam = searchParams.get('bg');
    const flowParam = searchParams.get('flow');
    const paypalStatus = searchParams.get('paypal_status');
    const paypalToken = searchParams.get('paypal_token');
    const payerId = searchParams.get('PayerID');
    const returnUrl = searchParams.get('return_url');
    
    // Detect wrapped PayPal flow
    const isWrappedPayPalFlow = flowParam === 'wrapped_paypal_vaulted';
    
    // Detect return from wrapped PayPal flow
    const isWrappedPayPalReturn = paypalStatus === 'vaulted_success' || paypalStatus === 'vaulted_cancelled';
    
    // Detect PayPal redirect back to checkout with native return URL (completing payment)
    const isPayPalCheckoutReturn = !!(paypalToken && payerId && returnUrl);
    
    const isWrappedFlow = isWrappedPayPalFlow || isWrappedPayPalReturn || isPayPalCheckoutReturn;
    const bgEnabled = !isWrappedFlow && isBackgroundEnabled(bgQueryParam, currentGame);

    if (!bgEnabled) {
      // When disabled, remove background image entirely (not just hide it)
      document.body.style.backgroundImage = 'none';
      document.body.setAttribute('data-bg', 'disabled');
      
      // Expand content to full viewport edges
      // Use dvh (dynamic viewport height) for mobile browser compatibility
      document.body.style.margin = '0';
      document.body.style.padding = '0';
      document.body.style.width = '100vw';
      document.body.style.height = '100dvh';
      document.body.style.maxWidth = '100%';
      document.body.style.maxHeight = '100dvh';
      document.body.style.overflow = 'hidden';
      
      // Make App container full viewport
      const appElement = document.querySelector('.App') as HTMLElement;
      if (appElement) {
        appElement.style.height = '100dvh';
        appElement.style.maxHeight = '100dvh';
        appElement.style.width = '100%';
        appElement.style.maxWidth = '100%';
        appElement.style.overflow = 'hidden';
      }
      
      // Make wrapper expand to edges
      const wrapperElement = document.querySelector('.wrapper') as HTMLElement;
      if (wrapperElement) {
        wrapperElement.style.borderRadius = '0';
        wrapperElement.style.width = '100%';
        wrapperElement.style.maxWidth = '100%';
        wrapperElement.style.height = '100dvh';
        wrapperElement.style.maxHeight = '100dvh';
        wrapperElement.style.overflow = 'auto';
      }
      
      return;
    }

    // Background is enabled, clear all inline styles to let CSS take over
    // This allows the original CSS (including landscape media queries) to work properly
    document.body.setAttribute('data-bg', 'enabled');
    document.body.style.margin = '';
    document.body.style.padding = '';
    document.body.style.width = '';
    document.body.style.height = '';
    document.body.style.maxWidth = '';
    document.body.style.maxHeight = '';
    document.body.style.overflow = '';
    
    const appElement = document.querySelector('.App') as HTMLElement;
    if (appElement) {
      appElement.style.height = '';
      appElement.style.maxHeight = '';
      appElement.style.width = '';
      appElement.style.maxWidth = '';
      appElement.style.overflow = '';
    }
    
    const wrapperElement = document.querySelector('.wrapper') as HTMLElement;
    if (wrapperElement) {
      wrapperElement.style.borderRadius = '';
      wrapperElement.style.width = '';
      wrapperElement.style.maxWidth = '';
      wrapperElement.style.height = '';
      wrapperElement.style.maxHeight = '';
      wrapperElement.style.overflow = '';
    }

    // Load the appropriate background image for enabled state
    let bgUrl = currentGame.themes?.default?.background_image_url;
    if (
      isLandscape &&
      currentGame.themes?.default?.background_image_landscape_url
    ) {
      bgUrl = currentGame.themes?.default?.background_image_landscape_url;
    }

    // Always set the background image when bg=enabled
    if (bgUrl) {
      document.body.style.backgroundImage = `url(${bgUrl})`;
    } else {
      // If no URL, clear any previous background
      document.body.style.backgroundImage = '';
    }
  }, [isLandscape, currentGame, searchParams]);

  const getSessionId = useCallback(() => {
    const pi = searchParams.get("pi");
    if (!pi) {
      throw new Error("Payment Intent ID is required");
    }
    return pi;
  }, [searchParams]);

  useEffect(() => {
    if (location.pathname === "/return" || location.pathname === "/return/") {
      return;
    }

    try {
      const sessionId = getSessionId();
      getCheckoutSession(sessionId)
        .then((data: unknown) => {
          setSessionData(data as CheckoutSessionData);
        })
        .catch((error) => {
          const axiosError = error as AxiosError;
          if (axiosError.response?.status === 401) {
            setIsUnauthorized(true);
          }
        });
    } catch {
      // Silent handling
    }
  }, [getSessionId]);

  useEffect(() => {
    if (sessionData?.stripe_pi_client_secret && !clientSecret) {
      setClientSecret(sessionData.stripe_pi_client_secret);
    }
  }, [sessionData, clientSecret]);

  return {
    currentGame,
    stripePromise,
    sessionData,
    clientSecret,
    isUnauthorized,
    isShortUrlResolving,
    shortUrlError,
  };
};
